import { identity3, orientationToMatrix, rotationY, type Mat3 } from '../dsp/geom3.ts';

/**
 * Where the phone is pointing, sampled while it turns.
 *
 * This is the sensor the whole map rests on, and it is worth being precise
 * about what is being asked of it. It is *not* being asked for a compass
 * bearing: nobody surveyed the room against true north, and a map rotated
 * bodily about the vertical axis is the same map. What is needed is the
 * change in heading between one chirp and the next, over the twenty seconds
 * of a scan, and that is the thing a fused orientation sensor is best at -
 * it comes from integrating a gyroscope, which is accurate over seconds and
 * only drifts over minutes.
 *
 * That is also why nothing here touches the accelerometer directly. Position
 * from acceleration means integrating twice, which turns a small bias into
 * metres of error within seconds and would corrupt every range in the scan.
 * The trajectory is instead reconstructed from orientation alone, on the
 * assumption that the user turned about a fixed axis with the phone held out
 * at a roughly constant radius - see `lookPosition`.
 */

export type OrientationSource = 'absolute' | 'relative' | 'assumed';

export interface OrientationStatus {
  source: OrientationSource;
  /** True once at least one event has arrived. */
  live: boolean;
  message: string;
}

export interface OrientationTracker {
  /** Device-to-world rotation for the moment `timeSec` into the scan. */
  at(timeSec: number): Mat3;
  status(): OrientationStatus;
  /** Total heading swept so far, radians - lets the UI show a progress ring. */
  sweptRad(): number;
  stop(): void;
}

interface Sample {
  timeSec: number;
  matrix: Mat3;
  heading: number;
}

export function orientationBlocker(): string | null {
  if (typeof window === 'undefined') return 'No browser environment.';
  if (!('DeviceOrientationEvent' in window)) {
    return 'This browser has no orientation sensor, so the scan cannot know which way the phone was pointing.';
  }
  return null;
}

/**
 * iOS requires an explicit grant, from inside a user gesture, and returns a
 * promise that rejects rather than resolving 'denied' if called outside one.
 */
export async function requestOrientationPermission(): Promise<boolean> {
  const anyEvent = DeviceOrientationEvent as unknown as {
    requestPermission?: () => Promise<'granted' | 'denied'>;
  };
  if (typeof anyEvent?.requestPermission !== 'function') return true;
  try {
    return (await anyEvent.requestPermission()) === 'granted';
  } catch {
    return false;
  }
}

export interface TrackerOptions {
  /**
   * Heading rate to assume if no sensor ever reports, radians per second.
   * A fallback, not a measurement: the scan then knows only that the user was
   * asked to turn once steadily, and the map is labelled accordingly.
   */
  assumedRateRadPerSec?: number;
  now?: () => number;
}

/**
 * Start listening. Prefers the absolute frame where the platform offers it,
 * because then two scans of the same room line up; falls back to the relative
 * frame, which is just as good within one scan and merely arbitrary between
 * them; and falls back again to assuming the user turned at the rate they
 * were asked to, which is much worse and is reported as such.
 */
export function trackOrientation(options: TrackerOptions = {}): OrientationTracker {
  const assumedRate = options.assumedRateRadPerSec ?? (2 * Math.PI) / 20;
  const now = options.now ?? (() => performance.now() / 1000);
  const startedAt = now();

  const samples: Sample[] = [];
  let source: OrientationSource = 'assumed';
  let live = false;
  let unwrapped = 0;
  let lastHeading: number | null = null;

  const onEvent = (event: DeviceOrientationEvent, absolute: boolean): void => {
    if (event.alpha === null || event.beta === null || event.gamma === null) return;
    live = true;
    if (absolute || event.absolute) source = 'absolute';
    else if (source !== 'absolute') source = 'relative';

    const matrix = orientationToMatrix(event.alpha, event.beta, event.gamma);
    // Heading of the phone's own -Z axis, tracked unwrapped so a full turn
    // reads as 2*pi rather than snapping back to zero at the seam.
    const heading = Math.atan2(-matrix[2], matrix[8]);
    if (lastHeading !== null) {
      let delta = heading - lastHeading;
      while (delta > Math.PI) delta -= 2 * Math.PI;
      while (delta < -Math.PI) delta += 2 * Math.PI;
      unwrapped += delta;
    }
    lastHeading = heading;
    samples.push({ timeSec: now() - startedAt, matrix, heading: unwrapped });
  };

  const absoluteListener = (e: Event): void => onEvent(e as DeviceOrientationEvent, true);
  const relativeListener = (e: Event): void => onEvent(e as DeviceOrientationEvent, false);

  if (typeof window !== 'undefined') {
    window.addEventListener('deviceorientationabsolute', absoluteListener);
    window.addEventListener('deviceorientation', relativeListener);
  }

  return {
    at(timeSec: number): Mat3 {
      if (samples.length === 0) return rotationY(assumedRate * timeSec);
      // Nearest sample rather than interpolated: orientation events arrive at
      // 60 Hz and the chirps at about three per second, so the nearest is
      // never more than 8 ms stale, and slerping two rotation matrices
      // correctly is a lot of machinery to buy 8 ms.
      let lo = 0;
      let hi = samples.length - 1;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (samples[mid].timeSec < timeSec) lo = mid + 1;
        else hi = mid;
      }
      const after = samples[lo];
      const before = samples[Math.max(0, lo - 1)];
      return Math.abs(after.timeSec - timeSec) < Math.abs(before.timeSec - timeSec)
        ? after.matrix
        : before.matrix;
    },
    status(): OrientationStatus {
      if (!live) {
        return {
          source: 'assumed',
          live: false,
          message:
            'No orientation sensor is reporting. The scan will assume you turned once at a steady rate, which places surfaces far less accurately.',
        };
      }
      return {
        source,
        live: true,
        message:
          source === 'absolute'
            ? 'Orientation locked to the compass.'
            : 'Orientation is relative - the map will be correct in shape but arbitrary in which way is north.',
      };
    },
    sweptRad(): number {
      return samples.length > 0 ? Math.abs(unwrapped) : assumedRate * (now() - startedAt);
    },
    stop(): void {
      if (typeof window === 'undefined') return;
      window.removeEventListener('deviceorientationabsolute', absoluteListener);
      window.removeEventListener('deviceorientation', relativeListener);
    },
  };
}

export const IDENTITY_ROTATION = identity3();
